import { GlobalPositionStrategy, OverlayRef } from '@angular/cdk/overlay';
import { Observable, Subject, filter, take } from 'rxjs';
import { DialogContainerComponent } from './dialog-container.component';

// Counter for unique dialog ids.
let uniqueId = 0;

/**
 * Reference to a dialog opened via the MdDialog service.
 */
export class DialogRef<T> {
    /** The instance of component opened into the dialog. */
    componentInstance: T;

    /** Whether the user is allowed to close the dialog. */
    disableClose = this.containerInstance.config.disableClose;

    /** Subject for notifying the user that the dialog has finished closing. */
    private afterClosed$: Subject<any> = new Subject();

    /** Subject for notifying the user that the dialog has started closing. */
    private beforeClose$: Subject<any> = new Subject();

    /** Result to be passed to afterClosed. */
    private result: any;

    constructor(
        private overlayRef: OverlayRef,
        public containerInstance: DialogContainerComponent,
        public readonly id: string = `oss-dialog-${uniqueId++}`
    ) {
        // Pass the id along to the container.
        this.containerInstance._id = id;

        this.containerInstance.animationStateChanged
            .pipe(
                filter(
                    (event) =>
                        event.phaseName === 'done' && event.toState === 'exit'
                ),
                take(1)
            )
            .subscribe(() => {
                this.overlayRef.dispose();
                this.afterClosed$.next(this.result);
                this.afterClosed$.complete();
                this.componentInstance = null!;
            });
    }

    /**
     * Close the dialog.
     * @param dialogResult Optional result to return to the dialog opener.
     */
    close(dialogResult?: any): void {
        // Transition the backdrop in parallel to the dialog.
        this.containerInstance.animationStateChanged
            .pipe(
                filter((event) => event.phaseName === 'start'),
                take(1)
            )
            .subscribe(() => {
                this.beforeClose$.next(dialogResult);
                this.beforeClose$.complete();
                this.overlayRef.detachBackdrop();
            });

        this.result = dialogResult;

        this.containerInstance.startExitAnimation();
    }

    /**
     * Gets an observable that is notified when the dialog is finished closing.
     */
    afterClosed(): Observable<any> {
        return this.afterClosed$.asObservable();
    }

    /**
     * Gets an observable that is notified when the dialog has started closing.
     */
    beforeClose(): Observable<any> {
        return this.beforeClose$.asObservable();
    }

    /**
     * Gets an observable that emits when the overlay's backdrop has been clicked.
     */
    backdropClick(): Observable<MouseEvent> {
        return this.overlayRef.backdropClick();
    }

    /**
     * Updates the dialog's position.
     * @param position New dialog position.
     */
    updatePosition(position?: any): this {
        let strategy = this.getPositionStrategy();

        if (position && (position.left || position.right)) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            position.left
                ? strategy.left(position.left)
                : strategy.right(position.right);
        } else {
            strategy.centerHorizontally();
        }

        if (position && (position.top || position.bottom)) {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            position.top
                ? strategy.top(position.top)
                : strategy.bottom(position.bottom);
        } else {
            strategy.centerVertically();
        }

        this.overlayRef.updatePosition();

        return this;
    }

    /**
     * Updates the dialog's width and height.
     * @param width New width of the dialog.
     * @param height New height of the dialog.
     */
    updateSize(width = 'auto', height = 'auto'): this {
        this.getPositionStrategy().width(width).height(height);
        this.overlayRef.updatePosition();
        return this;
    }

    /** Returns whether the dialog is animating. */
    isAnimating(): boolean {
        return this.containerInstance._isAnimating;
    }

    /** Fetches the position strategy object from the overlay ref. */
    private getPositionStrategy(): any {
        return this.overlayRef.getConfig()
            .positionStrategy as GlobalPositionStrategy;
    }
}
